import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../services/api';
import { ClipboardCheck, Loader, CheckCircle, XCircle, FileText, Download } from 'lucide-react';
import toast from 'react-hot-toast';

const TCI_FIELDS = [
  { key: 'tci_registration_cert', label: 'Valid trade registration certificate' },
  { key: 'tci_ntn_cert', label: 'National Tax Number certificate' },
  { key: 'tci_turnover_proof', label: 'Min. Rs 1M annual turnover (3yr audited)' },
  { key: 'tci_tax_filer_proof', label: 'FBR income tax return filer (3yr)' },
  { key: 'tci_srb_registration', label: 'Sindh Revenue Board registration' },
  { key: 'tci_non_blacklist_undertaking', label: 'Non-blacklist undertaking (stamp paper)' },
];

const SECTION_LABELS = { 'TC.I': 'Eligibility', 'TC.II': 'Facilities', 'TC.III': 'Experience', 'TC.IV': 'HR Capacity', 'TC.V': 'Linkages', 'TC.VI': 'Financial' };

const STATUS_STYLE = {
  'Field Pending': { bg: 'bg-gray-50', text: 'text-gray-600' },
  'Field Submitted': { bg: 'bg-amber-50', text: 'text-amber-700' },
  'Document Review Pending': { bg: 'bg-blue-50', text: 'text-blue-700' },
  'Finalized': { bg: 'bg-green-50', text: 'text-green-700' },
};

const fileUrl = (url) => `${api.defaults.baseURL.replace('/api', '')}${url}`;

const BaselineReview = () => {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [tci, setTci] = useState({});
  const [tcv, setTcv] = useState('');
  const [tcvi, setTcvi] = useState('');
  const [saving, setSaving] = useState(false);
  const [recommendation, setRecommendation] = useState('');
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => { fetchEvaluations(); }, []);

  const fetchEvaluations = async () => {
    setLoading(true);
    try {
      const res = await api.get('/baseline-evaluations');
      setEvaluations(res.data.evaluations || []);
    } catch (err) {
      toast.error('Failed to load baseline evaluations');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (evalRow) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/baseline-evaluations/${evalRow.id}`);
      setSelected(res.data.evaluation);
      setEvidence(res.data.evidence || []);
      const tciState = {};
      TCI_FIELDS.forEach(f => { tciState[f.key] = res.data.evaluation[f.key] || false; });
      setTci(tciState);
      setTcv(res.data.evaluation.tcv_score?.toString() || '');
      setTcvi(res.data.evaluation.tcvi_score?.toString() || '');
      setRecommendation('');
    } catch (err) {
      toast.error('Failed to load evaluation detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveDocumentSections = async () => {
    setSaving(true);
    try {
      await api.put(`/baseline-evaluations/${selected.id}/document-sections`, {
        ...tci,
        tcv_score: tcv ? parseInt(tcv, 10) : null,
        tcvi_score: tcvi ? parseInt(tcvi, 10) : null,
      });
      toast.success('Document sections saved');
      openDetail(selected);
      fetchEvaluations();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const allEligible = TCI_FIELDS.every(f => tci[f.key] === true);
  const canFinalize = allEligible && tcv && tcvi;

  const handleFinalize = async () => {
    if (!recommendation) return toast.error('Select Recommended or Not Recommended');
    setFinalizing(true);
    try {
      await api.post(`/baseline-evaluations/${selected.id}/finalize`, { recommendation });
      toast.success(`Evaluation finalized: ${recommendation}`);
      setSelected(null);
      fetchEvaluations();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to finalize');
    } finally {
      setFinalizing(false);
    }
  };

  const grandTotalPreview = (selected?.tcii_score || 0) + (selected?.tciii_score || 0) + (selected?.tciv_score || 0) + (parseInt(tcv, 10) || 0) + (parseInt(tcvi, 10) || 0);

  const downloadTrace = async (format) => {
    try {
      const res = await api.get(`/baseline-evaluations/${selected.id}/trace/${format}`, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `baseline_trace_${selected.reference_no}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download trace');
    }
  };

  return (
    <Layout title="Baseline Evaluations — Document Review">
      <div className="max-w-5xl mx-auto space-y-6">

        <div className="bg-blue-900 text-white rounded-xl p-5 flex items-center gap-3">
          <ClipboardCheck size={28} />
          <div>
            <h2 className="text-lg font-bold">Baseline Evaluations — Document Review</h2>
            <p className="text-blue-300 text-xs mt-0.5">
              Complete TC.I eligibility, TC.V linkages, TC.VI financial strength, then finalize the recommendation
            </p>
          </div>
        </div>

        {!selected ? (
          <div className="bg-white rounded-xl shadow border border-gray-100">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">
                All Evaluations <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{evaluations.length}</span>
              </h3>
            </div>
            {loading ? (
              <div className="p-8 text-center text-gray-400 flex items-center justify-center gap-2">
                <Loader size={16} className="animate-spin" /> Loading...
              </div>
            ) : evaluations.length === 0 ? (
              <div className="p-12 text-center">
                <ClipboardCheck size={40} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm">No baseline evaluations yet.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {evaluations.map(e => {
                  const s = STATUS_STYLE[e.status] || STATUS_STYLE['Field Pending'];
                  return (
                    <div
                      key={e.id}
                      className={`p-4 flex items-center gap-4 ${e.status === 'Document Review Pending' ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                      onClick={() => e.status !== 'Field Pending' && e.status !== 'Field Submitted' ? openDetail(e) : null}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 text-sm">{e.institution_name} <span className="text-gray-400 font-normal">· {e.reference_no}</span></p>
                        <p className="text-xs text-gray-500 mt-0.5">{e.district_name}</p>
                      </div>
                      {e.status === 'Finalized' && (
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${e.recommendation === 'Recommended' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                          {e.recommendation} · {e.grand_total}/100
                        </span>
                      )}
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${s.bg} ${s.text}`}>{e.status}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setSelected(null)} className="text-xs text-gray-400 hover:text-gray-600">← Back to list</button>
              <div className="flex gap-2">
                <button
                  onClick={() => downloadTrace('pdf')}
                  className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg font-medium"
                >
                  <Download size={12} /> PDF
                </button>
                <button
                  onClick={() => downloadTrace('pptx')}
                  className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg font-medium"
                >
                  <Download size={12} /> PPT
                </button>
              </div>
            </div>

            <h3 className="text-lg font-bold text-gray-800">{selected.institution_name}</h3>
            <p className="text-xs text-gray-500 mb-6">{selected.reference_no} · {selected.district_name}</p>

            {/* Field officer's submitted scores */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">TC.II Facilities</p>
                <p className="text-lg font-bold text-gray-800">{selected.tcii_score ?? '—'} / 35</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">TC.III Experience</p>
                <p className="text-lg font-bold text-gray-800">{selected.tciii_score ?? '—'} / 20</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">TC.IV HR Capacity</p>
                <p className="text-lg font-bold text-gray-800">{selected.tciv_score ?? '—'} / 25</p>
              </div>
            </div>

            {/* Evidence gallery */}
            {evidence.length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-2">Field Evidence ({evidence.length})</p>
                <div className="flex flex-wrap gap-2">
                  {evidence.map(ev => (
                    <a key={ev.id} href={fileUrl(ev.file_url)} target="_blank" rel="noreferrer" className="relative group">
                      {ev.file_type === 'photo' ? (
                        <img src={fileUrl(ev.file_url)} alt={ev.section} className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                      ) : (
                        <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
                          <FileText size={20} className="text-gray-400" />
                        </div>
                      )}
                      <span className="absolute -bottom-1 -right-1 bg-blue-800 text-white text-[9px] px-1 rounded">{ev.section}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {selected.status === 'Finalized' ? (
              <div className={`rounded-lg p-4 text-center ${selected.recommendation === 'Recommended' ? 'bg-green-50' : 'bg-red-50'}`}>
                <p className={`text-2xl font-bold ${selected.recommendation === 'Recommended' ? 'text-green-700' : 'text-red-700'}`}>
                  {selected.grand_total} / 100
                </p>
                <p className={`text-sm font-semibold mt-1 ${selected.recommendation === 'Recommended' ? 'text-green-700' : 'text-red-700'}`}>
                  {selected.recommendation}
                </p>
              </div>
            ) : (
              <>
                <div className="border-t border-gray-100 pt-5 mb-5">
                  <p className="text-sm font-semibold text-gray-700 mb-3">TC.I — Mandatory Eligibility (gate requirement)</p>
                  <div className="space-y-2">
                    {TCI_FIELDS.map(f => (
                      <label key={f.key} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!tci[f.key]}
                          onChange={e => setTci({ ...tci, [f.key]: e.target.checked })}
                          className="rounded"
                        />
                        {f.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">TC.V Forward Linkages (max 10)</label>
                    <input type="number" min="0" max="10" value={tcv} onChange={e => setTcv(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">TC.VI Financial Strength (max 10)</label>
                    <input type="number" min="0" max="10" value={tcvi} onChange={e => setTcvi(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </div>

                <button onClick={handleSaveDocumentSections} disabled={saving} className="mb-6 flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium">
                  {saving ? <Loader size={14} className="animate-spin" /> : null} Save Document Sections
                </button>

                <div className="border-t border-gray-100 pt-5">
                  <div className="bg-blue-50 rounded-lg p-4 mb-4 flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-800">Grand Total (preview)</span>
                    <span className="text-xl font-bold text-blue-800">{grandTotalPreview} / 100</span>
                  </div>

                  {!allEligible && (
                    <p className="text-xs text-red-600 mb-3">⚠ All TC.I eligibility items must be checked before finalizing.</p>
                  )}

                  <div className="flex gap-3 mb-4">
                    <button
                      onClick={() => setRecommendation('Recommended')}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium border-2 ${recommendation === 'Recommended' ? 'bg-green-600 text-white border-green-600' : 'border-gray-200 text-gray-600'}`}
                    >
                      <CheckCircle size={16} /> Recommended
                    </button>
                    <button
                      onClick={() => setRecommendation('Not Recommended')}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium border-2 ${recommendation === 'Not Recommended' ? 'bg-red-600 text-white border-red-600' : 'border-gray-200 text-gray-600'}`}
                    >
                      <XCircle size={16} /> Not Recommended
                    </button>
                  </div>

                  <button
                    onClick={handleFinalize}
                    disabled={!canFinalize || !recommendation || finalizing}
                    className="w-full bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white py-3 rounded-lg text-sm font-semibold"
                  >
                    {finalizing ? 'Finalizing...' : 'Finalize Evaluation'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

      </div>
    </Layout>
  );
};

export default BaselineReview;
